import { isNull, isNumber, isString } from 'lodash';

// String/number/boolean/null will be added to the URL, undefined will be dropped
type ParamValueType = string | number | boolean | null | undefined;

// Joins URL parts with single slashes. Preserves the scheme on a leading
// absolute URL (e.g. http://) and collapses adjacent slashes elsewhere.
export function urlJoin(...parts: string[]): string {
  return parts
    .filter(p => p !== '')
    .map((p, i) => (i === 0 ? p.replace(/\/+$/, '') : p.replace(/^\/+|\/+$/g, '')))
    .filter(p => p !== '')
    .join('/');
}

// TODO OA: Add unit tests
// The url needs to be full, i.e. partial paths are not supported
export function appendSearchParamsToUrl(
  urlString: string,
  paramsToAppend: { [key: string]: ParamValueType | ParamValueType[] } | undefined,
) {
  const url = new URL(urlString);
  const modifiedUrl = appendSearchParamsToUrlObject(url, paramsToAppend);
  return modifiedUrl.toString();
}

// Supports partial paths, not full URLs.
export function appendSearchParamsToUrlPath(
  path: string, // Needs to begin with "/". Can include existing search params.
  paramsToAppend: { [key: string]: ParamValueType | ParamValueType[] } | undefined,
) {
  const mockUrlBase = 'http://mock'; // Only used to get a valid URL object
  const fullUrl = new URL(`${mockUrlBase}${path}`);
  const modifiedUrl = appendSearchParamsToUrlObject(fullUrl, paramsToAppend);
  const search = modifiedUrl.search;
  return `${path}${search}`;
}

// String/number/null will be appended, undefined will be dropped
function appendSearchParamsToUrlObject(
  url: URL,
  paramsToAppend: { [key: string]: ParamValueType | ParamValueType[] } | undefined,
): URL {
  if (!paramsToAppend) return url;

  const searchParams = new URLSearchParams(url.search);
  const keys = Object.keys(paramsToAppend);
  for (const key of keys) {
    const value = paramsToAppend[key];
    if (Array.isArray(value)) {
      for (const element of value) {
        if (element === undefined) continue;
        searchParams.append(key, convertParamToString(element));
      }
    } else {
      if (value === undefined) continue;
      searchParams.append(key, convertParamToString(value));
    }
  }
  url.search = searchParams.toString();
  return url;
}

function convertParamToString(value: ParamValueType) {
  if (isNull(value)) return 'null';
  if (isNumber(value)) return value.toString();
  if (isString(value)) return value;
  if (typeof value === 'boolean') return value.toString();

  throw Error(`Unexpected param value ${value as any as string}`);
}
